import React, { useState, useEffect, useCallback } from 'react';
import { Trophy, Medal, Crown, Users, TrendingUp, Sparkles } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * ReferralLeaderboard - Shows top referrers to drive competition
 * Can be used standalone or embedded in the referral widget
 */
const ReferralLeaderboard = ({ userId, compact = false, limit = 10 }) => {
  const [leaderboard, setLeaderboard] = useState([]);
  const [userRank, setUserRank] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeframe, setTimeframe] = useState('all'); // 'all', 'month', 'week'

  const fetchLeaderboard = useCallback(async () => {
    try {
      const response = await fetch(
        `${API_URL}/api/referral/leaderboard?timeframe=${timeframe}&limit=${limit}`
      );
      const data = await response.json();
      if (data.success) {
        setLeaderboard(data.leaderboard || []);
        
        // Find user's rank if they're logged in
        if (userId && data.leaderboard) {
          const userIndex = data.leaderboard.findIndex(r => r.user_id === userId);
          if (userIndex !== -1) {
            setUserRank(userIndex + 1);
          } else if (data.user_rank) {
            setUserRank(data.user_rank);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching leaderboard:', error);
    } finally {
      setLoading(false);
    }
  }, [timeframe, limit, userId]);

  useEffect(() => {
    fetchLeaderboard();
  }, [fetchLeaderboard]);

  const getRankIcon = (rank) => {
    switch (rank) {
      case 1:
        return <Crown className="w-5 h-5 text-yellow-400" />;
      case 2:
        return <Medal className="w-5 h-5 text-gray-300" />;
      case 3:
        return <Medal className="w-5 h-5 text-orange-600" />;
      default:
        return <span className="w-5 h-5 flex items-center justify-center text-white/50 text-sm font-medium">{rank}</span>;
    }
  };

  const getRankBg = (rank, isCurrentUser) => {
    if (isCurrentUser) {
      return 'bg-gradient-to-r from-[#df1283]/30 to-[#FF7901]/30 border-[#df1283]/50';
    }
    switch (rank) {
      case 1:
        return 'bg-gradient-to-r from-yellow-500/20 to-yellow-600/10 border-yellow-500/30';
      case 2:
        return 'bg-gradient-to-r from-gray-400/20 to-gray-500/10 border-gray-400/30';
      case 3:
        return 'bg-gradient-to-r from-orange-600/20 to-orange-700/10 border-orange-600/30';
      default:
        return 'bg-white/5 border-white/10';
    }
  };

  if (loading) {
    return (
      <div className={`${compact ? '' : 'glass-card p-4'} animate-pulse`}>
        <div className="h-8 bg-white/10 rounded mb-4 w-1/2"></div>
        {[1, 2, 3].map(i => (
          <div key={i} className="h-12 bg-white/5 rounded mb-2"></div>
        ))}
      </div>
    );
  }

  if (leaderboard.length === 0) {
    return (
      <div className={`${compact ? '' : 'glass-card p-4'} text-center py-6`}>
        <Users className="w-10 h-10 text-white/30 mx-auto mb-2" />
        <p className="text-white/50 text-sm">No referrals yet. Be the first!</p>
      </div>
    );
  }

  return (
    <div className={compact ? '' : 'glass-card p-4'} data-testid="referral-leaderboard">
      {/* Header */}
      {!compact && (
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Trophy className="w-5 h-5 text-yellow-400" />
            <h3 className="text-white font-semibold">Top Referrers</h3>
          </div>
          
          {/* Timeframe Toggle */}
          <div className="flex gap-1 bg-white/5 rounded-lg p-0.5">
            {[
              { value: 'week', label: 'Week' },
              { value: 'month', label: 'Month' },
              { value: 'all', label: 'All Time' }
            ].map(t => (
              <button
                key={t.value}
                onClick={() => setTimeframe(t.value)}
                className={`px-2 py-1 text-xs rounded-md transition-all ${
                  timeframe === t.value
                    ? 'bg-[#df1283] text-white'
                    : 'text-white/50 hover:text-white'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Leaderboard List */}
      <div className="space-y-2">
        {leaderboard.slice(0, compact ? 5 : limit).map((referrer, index) => {
          const rank = index + 1;
          const isCurrentUser = userId && referrer.user_id === userId;
          
          return (
            <div
              key={referrer.user_id}
              className={`flex items-center gap-3 p-2.5 rounded-lg border transition-all ${getRankBg(rank, isCurrentUser)} ${
                isCurrentUser ? 'ring-1 ring-[#df1283]/50' : ''
              }`}
            >
              {/* Rank */}
              <div className="w-8 flex justify-center">
                {getRankIcon(rank)}
              </div>
              
              {/* Avatar */}
              <div 
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                  rank === 1 ? 'bg-yellow-500 text-black' :
                  rank === 2 ? 'bg-gray-400 text-black' :
                  rank === 3 ? 'bg-orange-600 text-white' :
                  'bg-gradient-to-br from-[#df1283] to-[#FF7901] text-white'
                }`}
              >
                {referrer.name?.[0]?.toUpperCase() || referrer.email?.[0]?.toUpperCase() || '?'}
              </div>
              
              {/* Name */}
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-medium truncate ${isCurrentUser ? 'text-[#df1283]' : 'text-white'}`}>
                  {isCurrentUser ? 'You' : (referrer.name || referrer.email?.split('@')[0] || 'Anonymous')}
                  {isCurrentUser && <Sparkles className="w-3 h-3 inline ml-1 text-[#FF7901]" />}
                </p>
              </div>
              
              {/* Referral Count */}
              <div className="flex items-center gap-1">
                <span className={`text-sm font-bold ${
                  rank === 1 ? 'text-yellow-400' :
                  rank === 2 ? 'text-gray-300' :
                  rank === 3 ? 'text-orange-500' :
                  'text-[#0FFFE2]'
                }`}>
                  {referrer.referral_count}
                </span>
                <span className="text-white/40 text-xs">refs</span>
              </div>
            </div>
          );
        })}
      </div>

      {/* User's Rank (if not in top list) */}
      {userId && userRank && userRank > (compact ? 5 : limit) && (
        <div className="mt-3 pt-3 border-t border-white/10">
          <div className="flex items-center gap-3 p-2.5 rounded-lg bg-gradient-to-r from-[#df1283]/20 to-[#FF7901]/20 border border-[#df1283]/30">
            <div className="w-8 flex justify-center">
              <span className="text-white/70 text-sm font-medium">#{userRank}</span>
            </div>
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#df1283] to-[#FF7901] flex items-center justify-center text-sm font-bold text-white">
              Y
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-[#df1283]">
                You <Sparkles className="w-3 h-3 inline ml-1 text-[#FF7901]" />
              </p>
            </div>
            <div className="flex items-center gap-1">
              <TrendingUp className="w-3 h-3 text-[#0FFFE2]" />
              <span className="text-white/50 text-xs">Keep referring!</span>
            </div>
          </div>
        </div>
      )}

      {/* Motivational Message */}
      {!compact && (
        <div className="mt-4 p-3 rounded-lg bg-gradient-to-r from-[#0FFFE2]/10 to-[#df1283]/10 border border-[#0FFFE2]/20">
          <p className="text-center text-sm text-white/70">
            {userRank && userRank <= 3 
              ? "🔥 You're in the top 3! Keep up the amazing work!"
              : userRank && userRank <= 10
              ? "💪 You're in the top 10! A few more referrals to climb higher!"
              : "📈 Refer friends to climb the leaderboard and win prizes!"}
          </p>
        </div>
      )}
    </div>
  );
};

export default ReferralLeaderboard;
